import { useState } from 'react';
import api, { AUTH_TOKEN_STORAGE_KEY } from '../../api/config';
import type { PracticeListItemType } from '../../providers/practices/PracticesListProvider';
import { clearPersistedQueryCache } from '../../providers/queryClient';
import { useUser } from '../../providers/UserProvider';
import { stashToast } from '../../utils/pendingToast';
import Loading from '../Loading';

interface PracticeActionCellProps {
    practice: PracticeListItemType;
}

export function PracticeActionCell({ practice }: PracticeActionCellProps) {
    const { user } = useUser();
    const [isLoading, setIsLoading] = useState(false);

    const setPractice = async (practiceId: string) => {
        if (!practiceId) return;
        setIsLoading(true);
        try {
            const { data } = await api.post(`/practices/set/${practiceId}`);
            // The rotated token is the only thing that busts the per-instance
            // auth cache; without it the reload is served the old practice.
            if (!data?.token) {
                throw new Error('Practice set returned no token');
            }
            localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, data.token);
            clearPersistedQueryCache();
            window.location.reload();
        } catch {
            // The switch may have committed before failing, so reload from a
            // cleared cache rather than leave one bucket holding two practices.
            stashToast('Failed to log in as this practice. Please try again.', 'error');
            clearPersistedQueryCache();
            window.location.reload();
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-end">
                <Loading />
            </div>
        );
    }

    if (!user?.database.data.isAdmin || !practice.id) {
        return null;
    }

    if (user.database.data.practices.includes(practice.id)) {
        return (
            <span className="text-sm font-normal leading-4 tracking-[0.005em] text-gray-800">
                Current practice
            </span>
        );
    }

    return (
        <button
            type="button"
            className="ml-auto flex flex-row justify-center items-center px-3 py-2 w-28 h-8 border border-gray-300 rounded-lg font-semibold text-sm text-gray-800 hover:bg-gray-100"
            onClick={() => setPractice(practice.id)}
        >
            Log in as
        </button>
    );
}
